import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ClipboardCopy,
  CheckCircle2,
  AlertTriangle,
  Sparkles,
  Upload,
  RefreshCw,
  Camera,
  FileUp,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import AdminBreadcrumb from '@/components/AdminBreadcrumb';
import logger from '@/utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// The exact prompt a maker can copy → paste into Gemini / ChatGPT / Claude.
// Producing valid JSON in this shape lets the importer accept it as-is.
const GEMINI_PROMPT = `You are helping a handmade jewellery brand called "Whispering Wristwear" prepare bulk product copy.

Return ONLY a JSON array (no prose, no markdown fence). Each item is one bracelet with these fields:

[
  {
    "name": "The 'Hope' Bracelet",
    "price": 19.95,
    "category": "bracelet",
    "occasions": ["graduation", "encouragement"],
    "image": "<optional public image URL>",
    "images": ["<optional gallery URL>", "..."],
    "inspiration": "A 1-3 sentence paragraph about the feeling behind the word.",
    "product_description": "1-2 sentences about the physical bracelet (19 cm, materials, clasp).",
    "gift_message": "A short, tender note to be read by the recipient.",
    "featured": false
  }
]

Rules:
- Use UK English, warm and quietly poetic. No exclamation marks.
- Each field max 240 characters.
- Occasions: a short array of lowercase single-word tags.
- If no image is available, omit the field — don't invent URLs.
- Return ONLY the JSON array.`;

const PLACEHOLDER = `[
  {
    "name": "The 'Hope' Bracelet",
    "price": 19.95,
    "category": "bracelet",
    "occasions": ["graduation", "encouragement"],
    "inspiration": "For the friend setting out into something new.",
    "product_description": "Handmade in Paphos. 19 cm bracelet on 7-strand steel wire.",
    "gift_message": "May hope be your quiet companion.",
    "featured": true
  }
]`;

function tryParseJson(text) {
  const trimmed = (text || '').trim();
  if (!trimmed) return { ok: false, error: 'Paste some JSON first.' };
  // Strip a wrapping ```json fence if present
  const fenced = trimmed
    .replace(/^```(?:json)?/i, '')
    .replace(/```$/i, '')
    .trim();
  try {
    const parsed = JSON.parse(fenced);
    if (!Array.isArray(parsed)) {
      // Sometimes the model wraps in {"products":[...]}; accept that too.
      if (parsed && Array.isArray(parsed.products)) {
        return { ok: true, value: parsed.products };
      }
      return { ok: false, error: 'JSON must be an array of products.' };
    }
    return { ok: true, value: parsed };
  } catch (e) {
    return { ok: false, error: `JSON parse error: ${e.message}` };
  }
}

export default function ProductBulkImportPage() {
  const [raw, setRaw] = useState('');
  const [plan, setPlan] = useState(null); // { ok, plan: [...], errors: [...], count, would_import, total }
  const [validating, setValidating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [done, setDone] = useState(null); // { count, plan, images_generated }
  const [generateImages, setGenerateImages] = useState(true);
  const fileInputRef = useRef(null);

  const parsed = useMemo(() => tryParseJson(raw), [raw]);

  const handleFilePicked = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large — please keep imports under 5 MB.');
      return;
    }
    try {
      const text = await file.text();
      setRaw(text);
      setPlan(null);
      setDone(null);
      toast.success(`Loaded ${file.name}`);
    } catch (err) {
      logger.error('File read failed', err);
      toast.error('Could not read that file.');
    } finally {
      // Reset so the same file can be re-picked after edits
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(GEMINI_PROMPT);
      toast.success('Prompt copied — paste it into Gemini.');
    } catch {
      toast.error('Could not copy. Please select and copy manually.');
    }
  };

  const validate = async () => {
    setPlan(null);
    setDone(null);
    if (!parsed.ok) {
      toast.error(parsed.error);
      return;
    }
    try {
      setValidating(true);
      const { data } = await axios.post(`${API}/products/bulk-import`, {
        products: parsed.value,
        dry_run: true,
      });
      setPlan(data);
      if (data.ok) {
        toast.success(`Ready to import ${data.count} product${data.count === 1 ? '' : 's'}.`);
      } else {
        toast.error(`${data.errors?.length || 0} row${data.errors?.length === 1 ? '' : 's'} need fixing.`);
      }
    } catch (err) {
      logger.error('Validate failed', err);
      toast.error(err?.response?.data?.detail || 'Validation failed');
    } finally {
      setValidating(false);
    }
  };

  const runImport = async () => {
    if (!parsed.ok) return;
    try {
      setImporting(true);
      const { data } = await axios.post(
        `${API}/products/bulk-import`,
        {
          products: parsed.value,
          dry_run: false,
          generate_images: generateImages,
        },
        { timeout: 180000 } // image generation can take time for many products
      );
      if (data.ok) {
        setDone({
          count: data.count,
          plan: data.plan,
          images_generated: data.images_generated || 0,
          images_failed: data.images_failed || 0,
        });
        toast.success(`Imported ${data.count} product${data.count === 1 ? '' : 's'}.`);
      } else {
        toast.error(`${data.errors?.length || 0} row${data.errors?.length === 1 ? '' : 's'} need fixing.`);
        setPlan(data);
      }
    } catch (err) {
      logger.error('Import failed', err);
      toast.error(err?.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const reset = () => {
    setRaw('');
    setPlan(null);
    setDone(null);
  };

  return (
    <div className="pt-28 pb-16 min-h-screen bg-[#F5F5F0]" data-testid="bulk-import-page">
      <div className="container-custom px-6 md:px-12 lg:px-24 max-w-6xl mx-auto">
        <AdminBreadcrumb current="Bulk import" />

        <div className="mb-10 max-w-3xl">
          <p className="text-caption text-[#84A98C] mb-2">Admin · Products</p>
          <h1 className="heading-display text-3xl md:text-4xl text-[#354F52] mb-3">
            Bulk import products
          </h1>
          <p className="text-[#52796F]">
            Generate product copy in Gemini, paste the JSON below, and the importer
            will create or update each bracelet in one click — including inspiration,
            product description and gift message.
          </p>
          <p className="text-xs text-[#84A98C] mt-3">
            Tip: to refine existing copy, click <strong>Download JSON</strong> on the{' '}
            <Link to="/admin/products" className="underline">Products admin</Link> first, paste it
            into Gemini with &ldquo;rewrite the gift messages to be more poetic&rdquo;, then paste the
            response back here.
          </p>
        </div>

        {/* Step 1: Prompt */}
        <section className="bg-white p-6 md:p-7 rounded-sm border border-[#CAD2C5]/40 mb-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37] font-serif">1</div>
            <div>
              <h2 className="font-serif text-xl text-[#354F52]">Copy the Gemini prompt</h2>
              <p className="text-xs text-[#84A98C] mt-1">Paste it into Gemini / ChatGPT / Claude, list the bracelet words, and let the model write all 3 notes per piece.</p>
            </div>
          </div>
          <pre
            className="text-xs leading-relaxed bg-[#F5F5F0] p-4 rounded-sm overflow-x-auto whitespace-pre-wrap text-[#354F52] max-h-72"
            data-testid="bulk-import-prompt"
          >
            {GEMINI_PROMPT}
          </pre>
          <Button
            type="button"
            onClick={copyPrompt}
            variant="outline"
            className="mt-4 flex items-center gap-2"
            data-testid="bulk-import-copy-prompt"
          >
            <ClipboardCopy className="w-4 h-4" strokeWidth={1.5} />
            Copy prompt
          </Button>
        </section>

        {/* Step 2: Paste */}
        <section className="bg-white p-6 md:p-7 rounded-sm border border-[#CAD2C5]/40 mb-6">
          <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37] font-serif">2</div>
              <div>
                <h2 className="font-serif text-xl text-[#354F52]">Paste — or drop in a file</h2>
                <p className="text-xs text-[#84A98C] mt-1">
                  Paste Gemini&apos;s reply, or upload a JSON file you previously exported with
                  &quot;Download JSON&quot;. The importer detects duplicates by name so re-imports
                  always update existing products instead of creating copies.
                </p>
              </div>
            </div>
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/json,.json"
                onChange={handleFilePicked}
                className="hidden"
                data-testid="bulk-import-file-input"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-2"
                data-testid="bulk-import-upload-file-btn"
              >
                <FileUp className="w-4 h-4" strokeWidth={1.5} />
                Upload JSON file
              </Button>
            </div>
          </div>
          <Textarea
            value={raw}
            onChange={(e) => {
              setRaw(e.target.value);
              setPlan(null);
              setDone(null);
            }}
            placeholder={PLACEHOLDER}
            rows={14}
            className="font-mono text-xs border-[#CAD2C5]"
            data-testid="bulk-import-textarea"
          />
          <div className="flex items-center justify-between mt-3 gap-3 flex-wrap">
            <p className="text-xs text-[#84A98C]" data-testid="bulk-import-parse-status">
              {raw.trim() === ''
                ? 'Waiting for JSON…'
                : parsed.ok
                ? `Parsed ${parsed.value.length} product${parsed.value.length === 1 ? '' : 's'}.`
                : parsed.error}
            </p>
            <div className="flex gap-2">
              <Button
                type="button"
                onClick={reset}
                variant="outline"
                className="flex items-center gap-2"
                data-testid="bulk-import-reset"
              >
                <RefreshCw className="w-4 h-4" strokeWidth={1.5} />
                Reset
              </Button>
              <Button
                type="button"
                onClick={validate}
                disabled={!parsed.ok || validating}
                className="btn-primary flex items-center gap-2"
                data-testid="bulk-import-validate"
              >
                <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                {validating ? 'Checking…' : 'Validate'}
              </Button>
            </div>
          </div>
        </section>

        {/* Step 3: Plan & Import */}
        {plan && (
          <section className="bg-white p-6 md:p-7 rounded-sm border border-[#CAD2C5]/40 mb-6" data-testid="bulk-import-plan">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37] font-serif">3</div>
              <div>
                <h2 className="font-serif text-xl text-[#354F52]">
                  {plan.ok ? 'Ready to import' : 'Fix these rows first'}
                </h2>
                <p className="text-xs text-[#84A98C] mt-1">
                  {plan.ok
                    ? `${plan.count} product${plan.count === 1 ? '' : 's'} — review, then confirm.`
                    : `${plan.errors?.length || 0} of ${plan.total} rows have problems. No products were written.`}
                </p>
              </div>
            </div>

            {plan.ok && plan.merged_into_existing && plan.merged_into_existing.length > 0 && (
              <div
                className="mb-5 p-4 rounded-sm bg-[#FAF6E8] border border-[#D4AF37]/30 text-sm text-[#52796F]"
                data-testid="bulk-import-merged-callout"
              >
                <p className="font-medium text-[#354F52] mb-1">
                  {plan.merged_into_existing.length} row{plan.merged_into_existing.length === 1 ? '' : 's'} matched an existing product by name.
                </p>
                <p className="text-xs text-[#84A98C]">
                  Instead of creating new duplicates, the importer will update the existing
                  product. Click each row below to verify before importing.
                </p>
              </div>
            )}

            {plan.ok && plan.plan && (
              <ul className="divide-y divide-[#CAD2C5]/40 mb-5" data-testid="bulk-import-plan-list">
                {plan.plan.map((row) => (
                  <li
                    key={row.id}
                    className="py-3 flex items-center justify-between gap-4"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <CheckCircle2 className="w-4 h-4 text-[#52796F] flex-shrink-0" strokeWidth={1.5} />
                      <div className="min-w-0">
                        <p className="font-serif text-[#354F52] truncate">{row.name}</p>
                        <p className="text-xs font-mono text-[#84A98C] truncate">{row.id}</p>
                      </div>
                    </div>
                    <span
                      className={`text-xs uppercase tracking-widest ${
                        row.matched_by_name
                          ? 'text-[#D4AF37]'
                          : row.action === 'insert'
                            ? 'text-[#D4AF37]'
                            : 'text-[#52796F]'
                      }`}
                      data-testid={row.matched_by_name ? `matched-by-name-${row.id}` : undefined}
                      title={row.matched_by_name ? 'Matched an existing product by name — will update, not duplicate' : undefined}
                    >
                      {row.matched_by_name
                        ? 'Matched · Update'
                        : row.action === 'insert'
                          ? 'New'
                          : 'Update'}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {!plan.ok && plan.errors && (
              <ul className="divide-y divide-[#CAD2C5]/40 mb-5" data-testid="bulk-import-errors-list">
                {plan.errors.map((e, i) => (
                  <li key={`${e.index}-${i}`} className="py-3 flex items-start gap-3">
                    <AlertTriangle className="w-4 h-4 text-[#BC4749] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                    <div>
                      <p className="text-sm text-[#354F52]">
                        Row {e.index + 1}
                        {e.name ? ` — "${e.name}"` : ''}
                      </p>
                      <p className="text-xs text-[#BC4749]">{e.error}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {plan.ok && (
              <div className="flex flex-col gap-4">
                {/* AI photo toggle */}
                {(() => {
                  const noImageCount = (plan.plan || []).length;  // every planned row that the backend will check
                  return noImageCount > 0 ? (
                    <label
                      className="flex items-start gap-3 p-3 bg-[#F5F5F0] rounded-sm cursor-pointer hover:bg-[#F5F5F0]/70 transition-colors"
                      data-testid="generate-images-toggle"
                    >
                      <input
                        type="checkbox"
                        checked={generateImages}
                        onChange={(e) => setGenerateImages(e.target.checked)}
                        className="mt-0.5 accent-[#D4AF37]"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Camera className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                          <span className="font-serif text-[#354F52]">
                            Generate an AI bracelet photo for products without one
                          </span>
                        </div>
                        <p className="text-xs text-[#84A98C] mt-1">
                          Uses Nano Banana to create a warm cream-linen lifestyle shot for each product, with beads encoding the bracelet name in Morse. Adds ~5–8 seconds per photo.
                        </p>
                      </div>
                    </label>
                  ) : null;
                })()}

                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    type="button"
                    onClick={runImport}
                    disabled={importing}
                    className="btn-primary flex items-center gap-2"
                    data-testid="bulk-import-confirm"
                  >
                    <Upload className="w-4 h-4" strokeWidth={1.5} />
                    {importing ? (generateImages ? 'Importing & rendering photos…' : 'Importing…') : `Import ${plan.count}`}
                  </Button>
                  <p className="text-xs text-[#84A98C]">
                    Updates overwrite existing fields for that id. New ids become new products.
                  </p>
                </div>
              </div>
            )}
          </section>
        )}

        {/* Done */}
        {done && (
          <section
            className="bg-[#52796F]/10 border border-[#52796F]/40 p-6 rounded-sm mb-6"
            data-testid="bulk-import-done"
          >
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-[#52796F] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
              <div>
                <p className="font-serif text-lg text-[#354F52]">
                  Imported {done.count} product{done.count === 1 ? '' : 's'} successfully.
                </p>
                {done.images_generated > 0 && (
                  <p className="text-sm text-[#52796F] mt-1" data-testid="bulk-import-images-count">
                    <Camera className="inline w-3.5 h-3.5 mr-1 -mt-0.5" strokeWidth={1.5} />
                    Rendered {done.images_generated} AI bracelet photo{done.images_generated === 1 ? '' : 's'}
                    {done.images_failed > 0 ? ` (${done.images_failed} failed — re-import those rows to retry)` : ''}.
                  </p>
                )}
                <p className="text-xs text-[#84A98C] mt-2">
                  <Link to="/admin/products" className="underline">Open the Products admin</Link>{' '}
                  to add more photos, set occasion tags or refine the copy.
                </p>
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
